import {useDraggable} from '@dnd-kit/core';
import {CSS} from '@dnd-kit/utilities';
import {differenceInMinutes, format, isPast} from 'date-fns';
import {useComponentsTheme} from 'platform/components';
import {getSize, Hide, HStack, Icon, Show, Text} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {CSSProperties} from 'react';

import {__, divide, pipe} from 'ramda';
import {ceil} from 'ramda-adjunct';

import {isNotNilOrZero, pushGtmEvent, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {EVENT_FILTER_WHEN_IS_PAST} from '../constants/eventFilterWhenIsPast';
import {useResizeHandler} from '../hooks/useResizeHandler';
import {BigCalendarEvent} from '../types/BigCalendarEvent';
import {BigCalendarEventWithLayout} from '../types/BigCalendarEventWithLayout';
import {isMultiDayEvent} from '../utils/isMultiDayEvent';
import {repeatingGradient} from '../utils/repeatingGradient';
import {resolveCalendarColor} from '../utils/resolveCalendarColor';
import {ResizeHandler} from './ResizeHandler';

interface EventItemProps extends RequiredTestIdProps {
  event: BigCalendarEventWithLayout;
  isOnWeekView?: boolean;
  isReadOnly?: boolean;
  top?: number;
  onClick?: (event: BigCalendarEvent) => void;
  enableHover?: boolean;
  onMouseEnter?: (event: BigCalendarEvent, mouseEvent: React.MouseEvent) => void;
  onMouseLeave?: (event: BigCalendarEvent) => void;
}

type EventItemStyle = CSSProperties & {
  '--event-filter': string;
};

const eventItemVariants = cva(
  'pointer-events-auto absolute cursor-pointer overflow-hidden opacity-100 [transform-origin:0_0] [will-change:transform,opacity,height]',
  {
    variants: {
      enableHover: {
        true: 'hover:[filter:brightness(0.85)_var(--event-filter)] hover:[transition:filter_0.2s_ease]',
        false: '',
      },
    },
  }
);

export function EventItem(props: EventItemProps) {
  const theme = useComponentsTheme();
  const {startDate, endDate, height} = useResizeHandler({event: props.event});

  const originalStart = props.event.originalStart ?? props.event.start;
  const originalEnd = props.event.originalEnd ?? props.event.end;

  const isMultiDay = isMultiDayEvent(originalStart, originalEnd);

  const displayStart = startDate;
  const displayEnd = endDate;

  const {attributes, listeners, setNodeRef, transform} = useDraggable({
    id: props.event.id,
    data: {
      type: 'draggable',
      eventId: props.event.id,
    },
    disabled: props.event.isReadOnly,
  });

  const onClick = () => {
    if (props.event.isReadOnly) {
      return;
    }

    pushGtmEvent({
      event: 'button_click',
      event_id: 'big_calendar_event_click',
      value: {
        eventId: props.event.id,
      },
    });

    props.onClick?.(props.event);
  };

  const onMouseEnter = (mouseEvent: React.MouseEvent) => {
    props.onMouseEnter?.(props.event, mouseEvent);
  };

  const onMouseLeave = () => {
    props.onMouseLeave?.(props.event);
  };

  /**
   * Calculates the duration of the event in minutes by getting the difference
   * between end and start times and converting from milliseconds
   */
  const durationInMinutes = differenceInMinutes(endDate, startDate);

  const eventColorHex = resolveCalendarColor(props.event.color || 'general.black', theme);
  const horizontalOffset = props.event.left ?? 0;
  const hasHorizontalOffset = isNotNilOrZero(horizontalOffset);
  const eventBorder = match([props.event.alternative, hasHorizontalOffset])
    .with([true, true], () => `1px solid ${theme.colors.general.black}`)
    .with([true, false], () => `2px solid ${eventColorHex}`)
    .with([false, true], () => `1px solid ${theme.colors.general.black}`)
    .with([false, false], () => 'none')
    .otherwise(() => 'none');
  const eventFilter = isPast(endDate) ? EVENT_FILTER_WHEN_IS_PAST : 'none';
  const style: EventItemStyle = {
    '--event-filter': eventFilter,
    top: props.top,
    left: `${horizontalOffset}%`,
    width: `${props.event.width ?? 100}%`,
    height,
    backgroundColor: props.event.alternative ? 'transparent' : eventColorHex,
    backgroundImage: props.event.alternative ? repeatingGradient(eventColorHex) : 'none',
    border: eventBorder,
    borderRadius: theme.radii.small,
    padding:
      durationInMinutes < MAX_DURATION_FOR_SINGLE_LINE
        ? `0 ${getSize(1)} 0 ${getSize(1)}`
        : getSize(1),
    transform: CSS.Transform.toString(transform),
    transformOrigin: '0 0',
    filter: eventFilter,
    willChange: 'transform, opacity, height',
    zIndex: hasHorizontalOffset ? dividerAndCeil(horizontalOffset) : 0,
  };

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={suffixTestId('eventItem', props)}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(eventItemVariants({enableHover: props.enableHover}))}
      style={style}
    >
      <Show when={durationInMinutes >= MAX_DURATION_FOR_SINGLE_LINE}>
        <HStack spacing={1} align="center" wrap="nowrap">
          <Show when={props.event.icon}>
            {(icon) => (
              <Icon
                data-testid={suffixTestId('eventItem-icon', props)}
                value={icon}
                size={3}
                color={props.event.alternative ? 'general.black' : 'general.white'}
              />
            )}
          </Show>
          <Text
            data-testid={suffixTestId('eventItem-title', props)}
            size="xSmall"
            color={props.event.alternative ? 'primary' : 'white'}
            alternative
            noWrap
          >
            {props.event.title}
          </Text>
          <Show when={durationInMinutes < MAX_DURATION_FOR_SINGLE_LINE}>
            <Text
              data-testid={suffixTestId('eventItem-time', props)}
              size="xSmall"
              color={props.event.alternative ? 'primary' : 'white'}
            >
              <Show when={isMultiDay}>
                {`${format(displayStart, 'd.M H:mm')} - ${format(displayEnd, 'd.M H:mm')}`}
              </Show>
              <Show when={!isMultiDay}>
                {format(displayStart, 'H:mm')} - {format(displayEnd, 'H:mm')}
              </Show>
            </Text>
          </Show>
        </HStack>

        <Text
          data-testid={suffixTestId('eventItem-time', props)}
          size="xSmall"
          color={props.event.alternative ? 'primary' : 'white'}
        >
          <Show when={isMultiDay}>
            {props.event.hideEndTime
              ? format(displayStart, 'd.M H:mm')
              : `${format(displayStart, 'd.M H:mm')} - ${format(displayEnd, 'd.M H:mm')}`}
          </Show>
          <Show when={!isMultiDay}>
            {props.event.hideEndTime
              ? format(displayStart, 'H:mm')
              : `${format(displayStart, 'H:mm')} - ${format(displayEnd, 'H:mm')}`}
          </Show>
        </Text>
      </Show>

      <Show when={durationInMinutes <= MAX_DURATION_FOR_SINGLE_LINE}>
        <HStack height="100%" spacing={1} align="center" wrap="nowrap">
          <Show when={props.event.icon}>
            {(icon) => (
              <Icon
                data-testid={suffixTestId('eventItem-icon', props)}
                value={icon}
                size={3}
                color={props.event.alternative ? 'general.black' : 'general.white'}
              />
            )}
          </Show>
          <Text
            data-testid={suffixTestId('eventItem-title', props)}
            size="xSmall"
            color={props.event.alternative ? 'primary' : 'white'}
            alternative
            noWrap
          >
            <Show when={isMultiDay}>
              {props.event.hideEndTime
                ? `${format(displayStart, 'd.M H:mm')} ${props.event.title}`
                : `${format(displayStart, 'd.M H:mm')} - ${format(displayEnd, 'd.M H:mm')} ${props.event.title}`}
            </Show>
            <Show when={!isMultiDay}>{`${format(displayStart, 'H:mm')} ${props.event.title}`}</Show>
          </Text>
        </HStack>
      </Show>
      <Hide
        when={props.event.isReadOnly || props.isReadOnly || !props.event.isActualEventEnd}
        onMobile
      >
        <ResizeHandler
          data-testid={suffixTestId('eventItem', props)}
          direction="bottom"
          eventId={props.event.id}
          isReadOnly={props.event.isReadOnly}
        />
      </Hide>
    </div>
  );
}

const MAX_DURATION_FOR_SINGLE_LINE = 42;
const dividerAndCeil = pipe(divide(__, 10), ceil);
